import type MP4Box from 'mp4box';
import { TrackEventProp } from '@studio/constants/tracking-constants';
import { SAMPLE_RATE_TABLE } from '../../constants';
import type { AddError, VideoUploadTrackEventProperties } from '../../types/upload-video';
import { DescriptorTag } from '../../types/upload-video';
import { BitReader } from '../BitReader';
import { hasAudio } from '../hasAudio';

const validateSampleRate = (
  file: MP4Box.MP4File,
  info: MP4Box.Mp4BoxInfo,
  addError: AddError,
  trackEventProperties: VideoUploadTrackEventProperties
): number => {
  if (!hasAudio(info)) {
    return 0;
  }

  const [audioTrack] = info.audioTracks;
  let sampleRate = audioTrack.audio.sample_rate;
  if (sampleRate === 0) {
    // The top level audio track sample rate is a 16-bit integer
    // and can not contain high sample rates like 96000 Hz.
    // Larger sample rates are stored in the DecoderSpecificInfo (defined in the AAC spec).

    const mp4aBox = file.moov?.traks[audioTrack.id - 1].mdia.minf.stbl.stsd.entries.find((box) => box.type === 'mp4a');

    if (mp4aBox === undefined) {
      throw new Error('Failed to find mp4a box');
    }

    const decoderConfig = mp4aBox.esds.esd.descs.find(
      (descriptor) => descriptor.tag === DescriptorTag.DecoderConfigDescriptorTag
    );
    if (decoderConfig === undefined) {
      throw new Error('Failed to find DecoderConfigDescriptor');
    }

    const decoderSpecificInfo = decoderConfig.descs.find(
      (descriptor) => descriptor.tag === DescriptorTag.DecoderSpecificInfoTag
    );

    if (decoderSpecificInfo === undefined) {
      throw new Error('Failed to find DecoderSpecificInfo');
    }

    if (decoderSpecificInfo.data === undefined) {
      throw new Error("Expected DecoderSpecificInfo to have a 'data' property; got undefined");
    }

    const bitReader = new BitReader(decoderSpecificInfo.data);

    const objectType = bitReader.read(5);
    // Read an extended object type if we get the escape value
    if (objectType === 0x1f) {
      bitReader.read(6);
    }

    // The DecoderSpecificInfo's sample rate is an index into a list of
    // pre-defined sample rates.
    const sampleRateIndex = bitReader.read(4);
    if (sampleRateIndex !== 0xf) {
      sampleRate = SAMPLE_RATE_TABLE[sampleRateIndex];
    } else {
      // Read an extended sample rate if we get the escape value
      sampleRate = bitReader.read(24);
    }
  }

  console.log(`Audio sample rate: ${sampleRate} Hz`);
  trackEventProperties[TrackEventProp.VIDEO_UPLOAD_AUDIO_SAMPLE_RATE_KHZ] = sampleRate;

  return sampleRate;
};

export default validateSampleRate;
